import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { fetchConfig } from '@theme/utilities';

(() => {
  const root = document.querySelector('[data-gv-bra]');
  const variantsEl = document.getElementById('gv-bra-variants');
  if (!root || !variantsEl) return;

  let variants = [];
  try {
    variants = JSON.parse(variantsEl.textContent || '[]');
  } catch (e) {
    return;
  }

  const form = document.getElementById('gv-bra-form');
  const optionEls = [...root.querySelectorAll('[data-gv-option]')];
  const variantInput = root.querySelector('[data-gv-variant-id]');
  const atcButtons = [...root.querySelectorAll('[data-gv-atc]')];
  const priceNowEls = [...root.querySelectorAll('[data-gv-price-now]')];
  const priceWasEls = [...root.querySelectorAll('[data-gv-price-was]')];
  const saveEls = [...root.querySelectorAll('[data-gv-save]')];
  const qtyInput = form?.querySelector('input[name="quantity"]');
  const packButtons = [...root.querySelectorAll('[data-gv-pack]')];
  const sticky = root.querySelector('[data-gv-sticky]');
  const stickyThumb = root.querySelector('[data-gv-sticky-thumb]');
  const stickyMeta = root.querySelector('[data-gv-sticky-meta]');
  const mainImg = root.querySelector('[data-gv-main-img]');
  const thumbs = root.querySelector('[data-gv-thumbs]');
  const sizeResult = root.querySelector('[data-gv-size-result]');
  const atcBase = root.dataset.atcLabel || 'Add to cart';

  let adding = false;
  let packQty = Number(qtyInput?.value || 1) || 1;

  const moneyFmt = (cents) => {
    try {
      return (cents / 100).toLocaleString(undefined, {
        style: 'currency',
        currency: (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD',
      });
    } catch (e) {
      return '$' + (cents / 100).toFixed(2);
    }
  };

  function selectedOptions() {
    return optionEls
      .sort((a, b) => Number(a.dataset.index) - Number(b.dataset.index))
      .map((el) => el.value);
  }

  function findVariant() {
    if (!optionEls.length) return variants[0];
    const opts = selectedOptions();
    return variants.find((v) => opts.every((o, i) => v[`option${i + 1}`] === o));
  }

  function syncCartCount(count) {
    if (typeof count !== 'number') return;
    document.querySelectorAll('[data-gv-cart-count]').forEach((el) => {
      el.textContent = String(count);
    });
  }

  function setBusy(busy) {
    adding = busy;
    atcButtons.forEach((btn) => {
      btn.disabled = busy || btn.dataset.soldOut === 'true';
    });
  }

  function openCartDrawer() {
    const drawer = document.querySelector('theme-drawer#cart-drawer');
    if (drawer && typeof drawer.open === 'function') {
      drawer.open();
      return;
    }
    if (window.Shopify?.actions?.openCart) {
      window.Shopify.actions.openCart();
    }
  }

  async function refreshCart() {
    const cartItemsComponent = document.querySelector('cart-items-component');
    if (cartItemsComponent?.fetchCartData) {
      await customElements.whenDefined('cart-items-component');
      return cartItemsComponent.fetchCartData();
    }
    const res = await fetch(`${Theme.routes.cart_url}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!res.ok) throw new Error(`Failed to fetch cart: ${res.status}`);
    return res.json();
  }

  function optionLabels() {
    return optionEls.map((el) => {
      const group = el.closest('[data-gv-option-group]');
      const on = group?.querySelector('[data-gv-swatch].is-on, [data-gv-size].is-on');
      return on?.dataset.label || on?.textContent?.trim() || el.value;
    });
  }

  function updatePackUi() {
    packButtons.forEach((btn) => {
      const q = Number(btn.dataset.gvPack);
      btn.classList.toggle('is-on', q === packQty);
      btn.setAttribute('aria-pressed', q === packQty ? 'true' : 'false');
    });
    if (qtyInput) qtyInput.value = String(packQty);
  }

  function sync() {
    const v = findVariant();
    if (!v || !variantInput) return;
    variantInput.value = v.id;

    const total = v.price * packQty;
    const compare = v.compare_at_price && v.compare_at_price > v.price ? v.compare_at_price * packQty : 0;
    const save = compare > total ? compare - total : 0;

    priceNowEls.forEach((el) => {
      el.textContent = moneyFmt(total);
    });
    priceWasEls.forEach((el) => {
      if (compare) {
        el.hidden = false;
        el.textContent = moneyFmt(compare);
      } else {
        el.hidden = true;
      }
    });
    saveEls.forEach((el) => {
      if (save) {
        el.hidden = false;
        el.textContent = `Save ${moneyFmt(save)}`;
      } else {
        el.hidden = true;
      }
    });

    const labels = optionLabels();
    if (stickyMeta) stickyMeta.textContent = `${packQty} × ${labels.join(' / ')}`;

    atcButtons.forEach((btn) => {
      btn.dataset.soldOut = v.available ? 'false' : 'true';
      btn.disabled = !v.available || adding;
      const showPrice = btn.hasAttribute('data-gv-atc-price');
      btn.textContent = v.available
        ? showPrice
          ? `${atcBase} · ${moneyFmt(total)}`
          : atcBase
        : 'Sold out';
    });

    if (v.featured_media) {
      if (mainImg) {
        mainImg.src = v.featured_media;
        mainImg.removeAttribute('srcset');
      }
      if (stickyThumb) {
        stickyThumb.src = v.featured_media;
        stickyThumb.removeAttribute('srcset');
      }
    }

    // Disable unavailable size/color combos
    root.querySelectorAll('[data-gv-option-group]').forEach((group) => {
      const index = Number(group.querySelector('[data-gv-option]')?.dataset.index || 0);
      const otherOpts = selectedOptions();
      group.querySelectorAll('[data-gv-swatch], [data-gv-size]').forEach((btn) => {
        const tryOpts = [...otherOpts];
        tryOpts[index] = btn.dataset.value;
        const match = variants.find((vr) => tryOpts.every((o, i) => vr[`option${i + 1}`] === o));
        const ok = match?.available !== false && !!match;
        btn.disabled = !ok;
        btn.classList.toggle('is-unavailable', !ok);
      });
    });

    updatePackUi();
  }

  async function addToCart(event) {
    event.preventDefault();
    if (!form || adding) return;
    const primary = atcButtons[0];
    if (primary?.disabled) return;

    const formData = new FormData(form);
    formData.set('quantity', String(packQty));
    const sectionIds = [];
    document.querySelectorAll('cart-items-component').forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionIds.push(item.dataset.sectionId);
      }
    });
    if (sectionIds.length) {
      formData.set('sections', [...new Set(sectionIds)].join(','));
      formData.set('sections_url', window.location.pathname);
    }

    const merchandiseId = String(formData.get('id') || '');
    if (!merchandiseId) return;

    const deferred = CartLinesUpdateEvent.createPromise();
    form.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId, quantity: packQty }],
        promise: deferred.promise,
      })
    );

    setBusy(true);
    try {
      const fetchCfg = fetchConfig('javascript', { body: formData });
      const response = await fetch(Theme.routes.cart_add_url, {
        ...fetchCfg,
        headers: {
          ...fetchCfg.headers,
          Accept: 'application/json',
        },
      }).then((res) => res.json());

      if (response.status) {
        form.dispatchEvent(
          new CartErrorEvent({
            error: response.message || 'Add to cart failed',
            code: 'INVALID',
            detail: {
              description: response.description,
              errors: response.errors,
            },
          })
        );
        const ajaxCart = await refreshCart().catch(() => null);
        deferred.resolve({
          cart: ajaxCart
            ? CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart)
            : { totalQuantity: 0, cost: {}, lines: [] },
          detail: { didError: true, items: ajaxCart?.items || [], source: 'glowvelle-bra', itemCount: packQty },
        });
        return;
      }

      const ajaxCart = await refreshCart();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
        detail: { didError: false, items: ajaxCart.items || [], source: 'glowvelle-bra', itemCount: packQty },
      });
      syncCartCount(ajaxCart.item_count);
      openCartDrawer();
    } catch (err) {
      deferred.reject(err);
    } finally {
      setBusy(false);
      sync();
    }
  }

  function selectOption(btn) {
    const group = btn.closest('[data-gv-option-group]');
    const input = group?.querySelector('[data-gv-option]');
    if (!input || btn.disabled) return;
    input.value = btn.dataset.value || '';
    group.querySelectorAll('[data-gv-swatch], [data-gv-size]').forEach((b) => {
      const on = b === btn;
      b.classList.toggle('is-on', on);
      b.setAttribute('aria-selected', on ? 'true' : 'false');
    });
    const labelEl = group.querySelector('[data-gv-option-label]');
    if (labelEl) labelEl.textContent = btn.dataset.label || btn.dataset.value || '';
    sync();
  }

  root.querySelectorAll('[data-gv-swatch], [data-gv-size]').forEach((btn) => {
    btn.addEventListener('click', () => selectOption(btn));
  });

  packButtons.forEach((btn) => {
    btn.addEventListener('click', () => {
      packQty = Number(btn.dataset.gvPack) || 1;
      sync();
    });
  });

  thumbs?.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-src]');
    if (!btn || !mainImg) return;
    mainImg.src = btn.dataset.src;
    mainImg.alt = btn.dataset.alt || '';
    mainImg.removeAttribute('srcset');
    thumbs.querySelectorAll('[data-src]').forEach((t) => t.classList.toggle('is-on', t === btn));
  });

  const buyBox = root.querySelector('[data-gv-buy]');
  if (sticky && buyBox && 'IntersectionObserver' in window) {
    const io = new IntersectionObserver(
      ([entry]) => {
        sticky.classList.toggle('is-on', !entry.isIntersecting);
      },
      { root: null, threshold: 0, rootMargin: '-80px 0px 0px 0px' }
    );
    io.observe(buyBox);
  }

  // Size finder → suggest M/L/XL/XXL and select matching size option
  const bandSel = root.querySelector('[data-gv-band]');
  const cupSel = root.querySelector('[data-gv-cup]');
  function suggestSize() {
    if (!bandSel || !cupSel || !sizeResult) return;
    const band = Number(bandSel.value);
    const cup = cupSel.value;
    const cupRank = { A: 1, B: 2, C: 3, D: 4, E: 5, F: 6 }[cup] || 3;
    let size = 'M';
    if (band >= 90 || cupRank >= 5) size = 'XXL';
    else if (band >= 85 || cupRank >= 4) size = 'XL';
    else if (band >= 75 || cupRank >= 3) size = 'L';
    else size = 'M';

    sizeResult.hidden = false;
    sizeResult.innerHTML = `Suggested size: <strong>${size}</strong>`;

    const sizeBtn = [...root.querySelectorAll('[data-gv-size]')].find((b) =>
      (b.dataset.label || '').toUpperCase().startsWith(size)
    );
    if (sizeBtn && !sizeBtn.disabled) selectOption(sizeBtn);
  }
  bandSel?.addEventListener('change', suggestSize);
  cupSel?.addEventListener('change', suggestSize);

  form?.addEventListener('submit', addToCart);
  atcButtons.forEach((btn) => {
    if (btn.type === 'submit') return;
    btn.addEventListener('click', (e) => {
      if (btn.hasAttribute('data-gv-scroll-buy')) return;
      e.preventDefault();
      addToCart(e);
    });
  });
  root.querySelectorAll('[data-gv-scroll-buy]').forEach((el) => {
    el.addEventListener('click', (e) => {
      e.preventDefault();
      buyBox?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  });

  sync();
})();
